import { Typography } from '@mui/material';
import { ReactNode, useCallback, useState } from 'react';
import { useHeaderBannerDataLazyQuery } from '~/layout/Dashboard/Header/graphql/generated/headerBannerData.gql.generated';

interface UsePaymentMethodBannerStateResult {
  hasPaymentMethods: boolean;
  renewalAlert: {
    message: ReactNode;
    daysUntilRebill: number;
  } | null;
  shouldPoll: boolean;
  checkPaymentMethods: () => Promise<void>;
}

export function usePaymentMethodBannerState(
  userId: number,
  onPaymentMethodsChange?: (hasPaymentMethods: boolean) => void,
): UsePaymentMethodBannerStateResult {
  const [hasPaymentMethods, setHasPaymentMethods] = useState(true);
  const [renewalAlert, setRenewalAlert] = useState<{
    message: ReactNode;
    daysUntilRebill: number;
  } | null>(null);
  const [shouldPoll, setShouldPoll] = useState(false);
  const [fetchPaymentMethodData] = useHeaderBannerDataLazyQuery();

  const checkPaymentMethods = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetchPaymentMethodData({
        variables: { userId },
        fetchPolicy: 'no-cache',
      });
      const { savedCards, userSubscriptions } = response.data ?? {};
      const nuveiSavedCards = response.data?.users?.nodes?.[0]?.nuveiSavedCards;
      const hasSavedCards = (savedCards?.nodes?.length ?? 0) > 0;
      const hasNuveiSavedCards = (nuveiSavedCards?.nodes?.length ?? 0) > 0;
      const activeSubscriptions =
        userSubscriptions?.nodes?.filter(
          (sub): sub is NonNullable<typeof sub> => sub !== null && sub.active === true,
        ) ?? [];
      const hasActiveSubscription = activeSubscriptions.length > 0;
      // Check for upcoming renewals
      if (hasActiveSubscription && !hasSavedCards) {
        const now = new Date();
        const upcomingRenewal = activeSubscriptions
          .map((sub) => (sub.rebillDate ? new Date(sub.rebillDate) : null))
          .filter((date): date is Date => date !== null)
          .sort((a, b) => a.getTime() - b.getTime())[0];
        if (upcomingRenewal) {
          const daysUntilRebill = Math.ceil(
            (upcomingRenewal.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
          );
          if (daysUntilRebill <= 20 && daysUntilRebill > 10) {
            setRenewalAlert({
              message: (
                <Typography component="span">
                  <Typography component="span" fontWeight="bold">
                    Your subscription renews soon.
                  </Typography>
                  {
                    ' To avoid any interruptions, please make sure you have an active payment method on file.'
                  }
                </Typography>
              ),
              daysUntilRebill,
            });
          } else if (daysUntilRebill <= 10 && daysUntilRebill > 3) {
            setRenewalAlert({
              message: (
                <Typography component="span">
                  <Typography component="span" fontWeight="bold">
                    👋 Just a quick reminder:
                  </Typography>
                  {
                    " your subscription renews soon, but we don't have a valid card on file. For your security, we recently cleared outdated payment methods."
                  }
                </Typography>
              ),
              daysUntilRebill,
            });
          } else if (daysUntilRebill <= 3) {
            setRenewalAlert({
              message: (
                <Typography component="span">
                  <Typography component="span" fontWeight="bold">
                    Your payment method is missing.
                  </Typography>
                  {' Add a card now to avoid losing access to your subscription benefits.'}
                </Typography>
              ),
              daysUntilRebill,
            });
          } else {
            setRenewalAlert(null);
          }
        } else {
          setRenewalAlert(null);
        }
      } else {
        setRenewalAlert(null);
      }
      const shouldShowBanner = !hasSavedCards && (hasNuveiSavedCards || hasActiveSubscription);
      setShouldPoll(hasActiveSubscription && !hasSavedCards);
      setHasPaymentMethods(!shouldShowBanner);
      onPaymentMethodsChange?.(!shouldShowBanner);
    } catch (error) {
      console.error('Error checking payment methods:', error);
    }
  }, [fetchPaymentMethodData, userId, onPaymentMethodsChange]);

  return { hasPaymentMethods, renewalAlert, shouldPoll, checkPaymentMethods };
}
